// Import `zo` directly (not via the '@/utils' barrel) to avoid a circular import
// that left it undefined when this schema is reached first via the mcp-chat entry.
import { zo } from '@/utils/zod';

export const createThemeConfigSchema = <T extends boolean>({
  optional: o,
}: {
  optional: T;
}) =>
  zo
    .object({
      colors: zo
        .object({
          primary: zo.string().optional(o),
          secondary: zo.string().optional(o),
          neutrals: zo
            .object({
              primary: zo
                .object({
                  900: zo.string().optional(o),
                  '900-disabled': zo.string().optional(o),
                  850: zo.string().optional(o),
                  750: zo.string().optional(o),
                })
                .optional(o),
              secondary: zo
                .object({
                  900: zo.string().optional(o),
                  750: zo.string().optional(o),
                })
                .optional(o),
            })
            .optional(o),
          surface: zo.string().optional(o),
          text: zo
            .object({
              primary: zo.string().optional(o),
              secondary: zo.string().optional(o),
              tertiary: zo.string().optional(o),
              disabled: zo.string().optional(o),
              inverse: zo.string().optional(o),
            })
            .optional(o),
          status: zo
            .object({
              success: zo.string().optional(o),
              warning: zo.string().optional(o),
              error: zo.string().optional(o),
              info: zo.string().optional(o),
            })
            .optional(o),
          chat: zo
            .object({
              userBubble: zo.string().optional(o),
              agentBubble: zo.string().optional(o),
              systemBubble: zo.string().optional(o),
            })
            .optional(o),
          border: zo
            .object({
              primary: zo.string().optional(o),
              secondary: zo.string().optional(o),
              focus: zo.string().optional(o),
              hover: zo.string().optional(o),
            })
            .optional(o),
        })
        .optional(o),
      spacing: zo
        .object({
          xs: zo.string().optional(o),
          sm: zo.string().optional(o),
          md: zo.string().optional(o),
          lg: zo.string().optional(o),
          xl: zo.string().optional(o),
          xxl: zo.string().optional(o),
          xxxl: zo.string().optional(o),
        })
        .optional(o),
      typography: zo
        .object({
          fontFamily: zo
            .object({
              primary: zo.string().optional(o),
              secondary: zo.string().optional(o),
              mono: zo.string().optional(o),
            })
            .optional(o),
          fontSize: zo
            .object({
              xs: zo.string().optional(o),
              sm: zo.string().optional(o),
              base: zo.string().optional(o),
              lg: zo.string().optional(o),
              xl: zo.string().optional(o),
              '2xl': zo.string().optional(o),
              '3xl': zo.string().optional(o),
            })
            .optional(o),
          fontWeight: zo
            .object({
              light: zo.number().optional(o),
              normal: zo.number().optional(o),
              medium: zo.number().optional(o),
              semibold: zo.number().optional(o),
              bold: zo.number().optional(o),
            })
            .optional(o),
          lineHeight: zo
            .object({
              tight: zo.number().optional(o),
              normal: zo.number().optional(o),
              relaxed: zo.number().optional(o),
            })
            .optional(o),
        })
        .optional(o),
      borderRadius: zo
        .object({
          none: zo.string().optional(o),
          sm: zo.string().optional(o),
          base: zo.string().optional(o),
          md: zo.string().optional(o),
          lg: zo.string().optional(o),
          xl: zo.string().optional(o),
          '2xl': zo.string().optional(o),
          max: zo.string().optional(o),
          full: zo.string().optional(o),
        })
        .optional(o),
      shadows: zo
        .object({
          none: zo.string().optional(o),
          sm: zo.string().optional(o),
          base: zo.string().optional(o),
          md: zo.string().optional(o),
          lg: zo.string().optional(o),
          xl: zo.string().optional(o),
          inner: zo.string().optional(o),
        })
        .optional(o),
      transitions: zo
        .object({
          fast: zo.string().optional(o),
          base: zo.string().optional(o),
          slow: zo.string().optional(o),
          slower: zo.string().optional(o),
        })
        .optional(o),
      zIndex: zo
        .object({
          base: zo.number().optional(o),
          elevated: zo.number().optional(o),
          sticky: zo.number().optional(o),
          overlay: zo.number().optional(o),
          modal: zo.number().optional(o),
          popover: zo.number().optional(o),
          tooltip: zo.number().optional(o),
          toast: zo.number().optional(o),
        })
        .optional(o),
      branding: zo
        .object({
          logo: zo
            .object({
              url: zo.string().optional(o),
              alt: zo.string().optional(o),
              width: zo.string().optional(o),
              height: zo.string().optional(o),
              position: zo.enum(['left', 'center', 'right']).optional(o),
            })
            .optional(o),
          avatars: zo
            .array(
              zo.object({
                url: zo.string().optional(o),
                alt: zo.string().optional(o),
                width: zo.string().optional(o),
                height: zo.string().optional(o),
              })
            )
            .optional(o),
          companyName: zo.string().optional(o),
          tagline: zo.string().optional(o),
          showBranding: zo.boolean().optional(o),
        })
        .optional(o),
    })
    .optional(o);
